import { type ListItemProps, Tooltip } from "@studio3t/generic-components";
import type { Encoding } from "@/types/api";
import { PROVIDER_LABELS } from "@/utils/providerCapabilities";
import { FooterPicker, FooterTrigger } from "./FooterPicker";
import { usePipelineStaging } from "./pipelineStaging";
import {
  ENCODING_LABELS,
  SOURCE_DELIVERS_OPTIONS,
  type SourceDeliversState,
  sourceDeliversState,
  sourceHasSchema,
  triggerLabel,
} from "./sourceDeliversState";

// The Source tab footer's "Source delivers" control (TTTL-268): sets the
// staged source's `input_encoding`, which lands in the pipeline code through
// the row-keyed extras (like the replication slot). Figma Bridge_PipelineCode
// StatusBar 16389-156752 (editable) and 16389-135789 (fixed).

type Shown = Exclude<SourceDeliversState, { kind: "hidden" }>;

/** The control's state and setter, or null while there is nothing to show
 *  (no source staged, or a provider that is never a source). Separate from
 *  the component so the editor can leave its status-bar extras out
 *  entirely: the status bar draws a divider in front of any extras it is
 *  given, which must not dangle before an empty control. */
export function useSourceDelivers(): {
  state: Shown;
  select: (value: Encoding) => void;
} | null {
  const { sources, schemas, pipelineExtras, setPipelineExtras } =
    usePipelineStaging();
  const source = sources[0];
  if (!source) return null;
  // The row's provider is the one source of truth, as for the generated
  // code's defaults and the Deploy gate: rows staged from the explorer
  // carry it, rows derived from code resolve it against the awaited
  // services list (StudioPipelineProvider's useServicesForDerivation).
  const provider = source.provider;
  const stored = pipelineExtras.inputEncodings?.[source.id];
  const schemaAttached = sourceHasSchema(schemas, source.id);
  const state = sourceDeliversState(
    provider,
    stored,
    schemaAttached,
    provider ? PROVIDER_LABELS[provider] : "",
  );
  if (state.kind === "hidden") return null;
  const select = (value: Encoding) =>
    setPipelineExtras({
      ...pipelineExtras,
      inputEncodings: { ...pipelineExtras.inputEncodings, [source.id]: value },
    });
  return { state, select };
}

export function SourceDeliversDropdown({
  state,
  select,
}: {
  state: Shown;
  select: (value: Encoding) => void;
}) {
  // The mock's trigger reads just the encoding ("Avro", "BSON"): the
  // accessible name keeps the "Source delivers" context the footer drops.
  const label = ENCODING_LABELS[state.value];
  const name = triggerLabel(state.value);
  if (state.kind === "fixed") {
    // A fixed encoding is a disabled subtle button with no chevron: there is
    // nothing to choose. The reason lives in a tooltip, which is why the
    // trigger is aria-disabled rather than natively disabled.
    return (
      <Tooltip text={state.reason} side="top">
        <FooterTrigger
          type="button"
          aria-disabled="true"
          aria-label={name}
          data-testid="source-delivers"
        >
          {label}
        </FooterTrigger>
      </Tooltip>
    );
  }
  const items: ListItemProps[][] = [
    SOURCE_DELIVERS_OPTIONS.map((value) => ({
      type: "text" as const,
      id: `source-delivers-${value}`,
      value,
      label: ENCODING_LABELS[value],
      selected: { value: value === state.value, icon: "dot" as const },
      onClick: () => select(value),
    })),
  ];
  // The Avro-without-schema warning is the Source tab's alert under the
  // schema slot (StageNavPanel), where the schema is attached.
  return (
    <FooterPicker
      label={label}
      name={name}
      items={items}
      testId="source-delivers"
    />
  );
}
